import React, { createContext, useContext, useState, useCallback } from 'react';
import NotificationModal from '../components/NotificationModal';

interface NotificationState {
    isOpen: boolean;
    type: 'info' | 'success' | 'warning' | 'error';
    title: string;
    message: string;
    onConfirm?: () => void;
    confirmText?: string;
    cancelText?: string;
    showCancel?: boolean;
    showGoToSubscription?: boolean;
    onGoToSubscription?: () => void;
}

const NotificationContext = createContext<{
    showNotification: (config: Omit<NotificationState, 'isOpen'>) => void;
    showConfirm: (config: Omit<NotificationState, 'isOpen' | 'type' | 'showCancel'> & { onConfirm: () => void }) => void;
} | null>(null);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [notification, setNotification] = useState<NotificationState>({
        isOpen: false,
        type: 'info',
        title: '',
        message: ''
    });

    const showNotification = useCallback((config: Omit<NotificationState, 'isOpen'>) => {
        setNotification({
            ...config,
            isOpen: true
        });
    }, []);

    const showConfirm = useCallback((config: Omit<NotificationState, 'isOpen' | 'type' | 'showCancel'> & { onConfirm: () => void }): void => {
        const handleConfirm = () => {
            config.onConfirm();
        };

        setNotification({
            isOpen: true,
            type: 'warning',
            showCancel: true,
            title: config.title,
            message: config.message,
            confirmText: config.confirmText || 'OK',
            cancelText: config.cancelText || 'Anuluj',
            onConfirm: handleConfirm
        });
    }, []);

    const closeNotification = useCallback(() => {
        setNotification(prev => ({ ...prev, isOpen: false }));
    }, []);

    return (
        <NotificationContext.Provider value={{ showNotification, showConfirm }}>
            {children}
            <NotificationModal
                isOpen={notification.isOpen}
                type={notification.type}
                title={notification.title}
                message={notification.message}
                onClose={closeNotification}
                onConfirm={notification.onConfirm}
                confirmText={notification.confirmText}
                cancelText={notification.cancelText}
                showCancel={notification.showCancel}
                showGoToSubscription={notification.showGoToSubscription}
                onGoToSubscription={notification.onGoToSubscription}
            />
        </NotificationContext.Provider>
    );
};

export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification must be used within NotificationProvider');
    }
    return context;
};

